import { Controller } from "@hotwired/stimulus"

/**
 * Hero booking form controller — captures dive preference, dates, and pax count,
 * then redirects to the self-service booking wizard with prepopulated params.
 */
export default class extends Controller {
  static targets = ["diveSelect", "datePicker", "pax", "paxDisplay"]

  static values = {
    wizardUrl: String
  }

  connect() {
    this.selectedDiveOption = ""
  }

  handleDiveOptionSelection() {
    this.selectedDiveOption = this.diveSelectTarget.value
  }

  incrementPax() {
    const current = parseInt(this.paxTarget.value, 10) || 1
    if (current < 20) this._setPax(current + 1)
  }

  decrementPax() {
    const current = parseInt(this.paxTarget.value, 10) || 1
    if (current > 1) this._setPax(current - 1)
  }

  _setPax(value) {
    this.paxTarget.value = value
    if (this.hasPaxDisplayTarget) this.paxDisplayTarget.textContent = value
  }

  // ===== Redirect =====

  redirectToWizard() {
    if (!this.validate()) return

    const params = new URLSearchParams()

    if (this.selectedDiveOption) {
      params.set("dive_option", this.selectedDiveOption)
    }

    const pax = parseInt(this.paxTarget.value, 10) || 1
    params.set("pax", pax)

    // Read selected dates from the hero-date-picker controller
    const datePickerController = this.application.getControllerForElementAndIdentifier(
      this.datePickerTarget, "hero-date-picker"
    )
    if (datePickerController) {
      datePickerController.getSelectedDates().forEach(date => {
        params.append("dates[]", date)
      })
    }

    window.location.href = `${this.wizardUrlValue}?${params.toString()}`
  }

  validate() {
    let valid = true
    let firstInvalid = null

    // Validate dive selection
    if (!this.diveSelectTarget.value) {
      this.diveSelectTarget.classList.add("is-invalid")
      if (!firstInvalid) firstInvalid = this.diveSelectTarget
      valid = false
    } else {
      this.diveSelectTarget.classList.remove("is-invalid")
    }

    // Validate at least one date selected
    const datePickerController = this.application.getControllerForElementAndIdentifier(
      this.datePickerTarget, "hero-date-picker"
    )
    const dates = datePickerController ? datePickerController.getSelectedDates() : []
    const trigger = this.datePickerTarget.querySelector("[data-hero-date-picker-target='trigger']")
    if (dates.length === 0) {
      if (trigger) trigger.classList.add("is-invalid")
      if (!firstInvalid) firstInvalid = trigger
      valid = false
    } else {
      if (trigger) trigger.classList.remove("is-invalid")
    }

    // Validate pax
    const pax = parseInt(this.paxTarget.value, 10)
    if (!pax || pax < 1) {
      this.paxTarget.classList.add("is-invalid")
      if (!firstInvalid) firstInvalid = this.paxTarget
      valid = false
    } else {
      this.paxTarget.classList.remove("is-invalid")
    }

    if (firstInvalid) {
      firstInvalid.focus()
      firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" })
    }

    return valid
  }
}
